/**
 * 分镜状态轮询：每 5 秒拉取一次分镜的 keyframe + video 状态。
 *
 * 使用场景：
 * - 用户点了"生成新候选"或"激活视频"后，UI 需要持续刷新看到状态变化
 * - 跨多个分镜时，组件可在 selected 切换时启动/停止
 *
 * 默认只展示最近 12 张候选；调用方可通过 limit 参数调大（spec 7.9 「查看更多」）
 *
 * 设计决策：候选生成中的视觉反馈由候选缩略图自身承担（status 非 completed
 * 时网格显示 loading 占位），无需在 hook 层暴露 "generating" 字段控制按钮
 * 可用性——按钮本身始终可点击以支持连续点击发起多张并行生成。
 */

import { useEffect, useState } from 'react';
import { directorApi, type DirectorImageJob } from '../services/directorApi';

export interface ShotPollingState {
  keyframes: DirectorImageJob[];
  hasMore: boolean;
}

export function useShotPolling(
  shotId: string | null,
  options: { enabled?: boolean; intervalMs?: number; limit?: number } = {}
): ShotPollingState {
  const [keyframes, setKeyframes] = useState<DirectorImageJob[]>([]);
  const [hasMore, setHasMore] = useState(false);
  const intervalMs = options.intervalMs ?? 5000;
  const enabled = options.enabled ?? true;
  const limit = options.limit;

  useEffect(() => {
    if (!shotId || !enabled) {
      setKeyframes([]);
      setHasMore(false);
      return;
    }

    let cancelled = false;
    let timer: NodeJS.Timeout | null = null;

    const tick = async () => {
      try {
        const result = await directorApi.listKeyframes(shotId, limit ? { limit } : {});
        if (cancelled) return;
        setKeyframes(result.jobs);
        setHasMore(result.hasMore);
      } catch (err) {
        // 静默失败，下次再试
        console.error('[useShotPolling]', err);
      } finally {
        if (!cancelled) {
          timer = setTimeout(tick, intervalMs);
        }
      }
    };

    tick();

    return () => {
      cancelled = true;
      if (timer) clearTimeout(timer);
    };
  }, [shotId, enabled, intervalMs, limit]);

  return { keyframes, hasMore };
}
